// src/App.js
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';

// Kunden-Komponenten
import CustomersPage from './pages/CustomersPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import CustomerEditPage from './pages/CustomerEditPage';

// Projekt-Komponenten
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import ProjectEditPage from './pages/ProjectEditPage';

// Weitere Komponenten
import TimeTrackingPage from './pages/TimeTrackingPage';
import MaterialsPage from './pages/MaterialsPage';
import MaterialsManagementPage from './pages/MaterialsManagementPage';
import EmployeesPage from './pages/EmployeesPage';
import ReportsPage from './pages/ReportsPage';
import OffersPage from './pages/OffersPage';
import ServicesPage from './pages/ServicesPage'; 
import CompanyProfilePage from './pages/CompanyProfilePage';
import Layout from './components/layout/Layout';

// Imports für die Rechnungs-Seiten
import InvoicesPage from './pages/InvoicesPage';
import InvoiceDetailPage from './pages/InvoiceDetailPage';
import CreateInvoicePage from './pages/CreateInvoicePage';
import InvoiceEditPage from './pages/InvoiceEditPage';
import ManualInvoicePage from './pages/ManualInvoicePage';

// Import für die Report-Detail-Seite
import ReportDetailPage from './pages/ReportDetailPage';

// Verbesserte PrivateRoute mit zusätzlichem Debugging und Fehlerbehandlung
const PrivateRoute = ({ children }) => {
  const [isValidating, setIsValidating] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  useEffect(() => {
    const validateToken = () => {
      const token = localStorage.getItem('token');
      console.log('PrivateRoute überprüft Token:', token ? 'Vorhanden' : 'Fehlt');
      
      if (!token) {
        setIsAuthenticated(false);
        setIsValidating(false);
        return;
      }
      
      // Hier könnten Sie eine API-Anfrage senden, um den Token zu validieren
      // Für jetzt nehmen wir an, dass jeder vorhandene Token gültig ist
      setIsAuthenticated(true);
      setIsValidating(false);
    };
    
    validateToken();
  }, []);
  
  // Zeige einen Lade-Indikator während der Validierung
  if (isValidating) {
    return <div>Überprüfe Authentifizierung...</div>;
  }
  
  // Wenn der Benutzer nicht authentifiziert ist, leite zur Login-Seite um
  if (!isAuthenticated) {
    console.log('PrivateRoute: Nicht authentifiziert, leite zur Login-Seite um');
    return <Navigate to="/login" />;
  }
  
  // Wenn der Benutzer authentifiziert ist, zeige die Kinder-Komponenten
  return children;
};

function App() {
  return (
    <Router>
      <Routes>
        {/* Root-Route hinzufügen, die zum Dashboard umleitet, wenn authentifiziert, sonst zum Login */}
        <Route path="/" element={
          localStorage.getItem('token') 
            ? <Navigate to="/dashboard" /> 
            : <Navigate to="/login" />
        } />
        
        <Route path="/login" element={<LoginPage />} />
        
        <Route path="/dashboard" element={
          <PrivateRoute>
            <Layout>
              <DashboardPage />
            </Layout>
          </PrivateRoute>
        } />
        
        {/* Kunden-Routen */}
        <Route path="/kunden" element={
          <PrivateRoute>
            <Layout>
              <CustomersPage />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/kunden/:id" element={
          <PrivateRoute>
            <Layout>
              <CustomerDetailPage />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/kunden/:id/edit" element={
          <PrivateRoute>
            <Layout>
              <CustomerEditPage />
            </Layout>
          </PrivateRoute>
        } />
        
        {/* Projekt-Routen */}
        <Route path="/projekte" element={
          <PrivateRoute>
            <Layout>
              <ProjectsPage />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/projekte/:id" element={
          <PrivateRoute>
            <Layout>
              <ProjectDetailPage />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/projekte/:id/edit" element={
          <PrivateRoute>
            <Layout>
              <ProjectEditPage />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/zeiterfassung" element={
          <PrivateRoute>
            <Layout>
              <TimeTrackingPage />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/materialien" element={
          <PrivateRoute>
            <Layout>
              <MaterialsPage />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/materials/manage" element={
          <PrivateRoute>
            <Layout>
              <MaterialsManagementPage />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/services" element={
          <PrivateRoute>
            <Layout>
              <ServicesPage />
            </Layout>
          </PrivateRoute>
        } />
        
        {/* Neue Route für das Unternehmensprofil */}
        <Route path="/unternehmensprofil" element={
          <PrivateRoute>
            <Layout>
              <CompanyProfilePage />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/employees" element={
          <PrivateRoute>
            <Layout>
              <EmployeesPage />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/rapporte" element={
          <PrivateRoute>
            <Layout>
              <ReportsPage />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/rapporte/:id" element={
          <PrivateRoute>
            <Layout>
              <ReportDetailPage />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/offerten" element={
          <PrivateRoute>
            <Layout>
              <OffersPage />
            </Layout>
          </PrivateRoute>
        } />
        
        {/* Routen für Rechnungen */}
        <Route path="/rechnungen" element={
          <PrivateRoute>
            <Layout>
              <InvoicesPage />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/rechnungen/:id" element={
          <PrivateRoute>
            <Layout>
              <InvoiceDetailPage />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/rechnungen/erstellen/:reportId" element={
          <PrivateRoute>
            <Layout>
              <CreateInvoicePage />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/rechnungen/:id/bearbeiten" element={
          <PrivateRoute>
            <Layout>
              <InvoiceEditPage />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/rechnungen/neu/manuell" element={
          <PrivateRoute>
            <Layout>
              <ManualInvoicePage />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
